/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Card,
  CardBody,
  Code,
  Flex,
  Heading,
  Input,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  TableContainer,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { getAuth } from 'firebase/auth';
import { app } from '../../../../configs/firebaseApp';
import { useAppSelector } from '../../../../redux/hooks';
import { getSelectedManualVerificationSessionProviderData } from '../../../../redux/manualVerification/providerDetails/selectors';
import { setSelectRequestScreenSearchQuery } from '../../../../redux/manualVerification/sessionsDetails';
import { updateActiveStep as updateManualVerificationActiveStep } from '../../../../redux/manualVerification/stepperDetails';
import { resetSelectedInstances } from '../../../../redux/providerBuilder/selectedInstances';
import { setSelectedNetworkRequest } from '../../../../redux/providerBuilder/selectedNetworkRequest';
import { getUserType } from '../../../../redux/user/selectors';
import { NetworkRequest } from '../../../../utils/types';
import { isAdmin } from '../../../../utils/utils';

interface NetworkRequestsTableProps {
  searchQueryInput: string;
  handleSearch: (searchString: string) => void;
  filteredNetworkRequestStore: NetworkRequest[];
}

const NetworkRequestsTable: React.FC<NetworkRequestsTableProps> = ({
  searchQueryInput,
  handleSearch,
  filteredNetworkRequestStore,
}) => {
  const auth = getAuth(app);
  const userType = useAppSelector(getUserType);
  const selectedManualVerificationSessionProviderData = useAppSelector(
    getSelectedManualVerificationSessionProviderData
  );

  const toast = useToast();

  const pagesVisited = useMemo(
    () =>
      filteredNetworkRequestStore?.filter(
        (network) => network.contentType.includes('html') && network.method === 'GET'
      )?.length ?? 0,
    [filteredNetworkRequestStore]
  );

  const dispatch = useDispatch();

  function handleRequestClick(networkRequest: any) {
    if (
      !(
        isAdmin(userType) ||
        auth.currentUser?.uid === selectedManualVerificationSessionProviderData.creatorUid
      )
    ) {
      return toast({
        title: 'Not Allowed',
        description: 'Only application owners can edit the provider',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }

    const tempNetworkRequest = {
      ...networkRequest,
      response: { body: networkRequest.responseBody },
      request: { body: networkRequest.requestBody },
    };
    dispatch(setSelectedNetworkRequest(tempNetworkRequest));
    dispatch(resetSelectedInstances());
    createProviderHandle();
  }

  function createProviderHandle(): void {
    dispatch(setSelectRequestScreenSearchQuery(searchQueryInput));
    dispatch(updateManualVerificationActiveStep(4));
  }

  return (
    <>
      <Card variant="outline" borderRadius={10} size="lg" borderBottomRadius={0}>
        <CardBody position="relative">
          <Box width="100%" height="100%">
            <VStack w="full">
              <Box w="full">
                <Flex>
                  <Box flex={0.8}>
                    <Heading className="medium-font" mb={2}>
                      Select the URL for the provider
                    </Heading>
                    <Text className="small-font" lineHeight="24px" color="#98A2B3">
                      The url response you select must contain the data <br /> you are
                      looking to prove
                    </Text>
                  </Box>
                  <Box mb={2} flex={1}>
                    <Flex gap={5}>
                      <Card
                        variant="outline"
                        flex={1}
                        borderRadius={10}
                        height="fit-content"
                      >
                        <CardBody pl={4} pb={2}>
                          <Stat>
                            <StatLabel
                              mb={1}
                              className="medium-font"
                              color="#667085"
                              fontWeight={600}
                            >
                              Pages visited
                            </StatLabel>
                            <StatNumber>
                              <Flex alignItems="center" gap={2}>
                                <Text className="large-font">{pagesVisited}</Text>
                              </Flex>
                            </StatNumber>
                            <StatHelpText
                              mt={1}
                              fontWeight={400}
                              className="x-small-font"
                              color="gray.600"
                            >
                              Number of unique pages visited
                            </StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                      <Card
                        variant="outline"
                        flex={1}
                        borderRadius={10}
                        height="fit-content"
                      >
                        <CardBody pl={4} pb={2}>
                          <Stat>
                            <StatLabel
                              mb={1}
                              className="medium-font"
                              color="#667085"
                              fontWeight={600}
                            >
                              Requests captured
                            </StatLabel>
                            <StatNumber>
                              <Flex alignItems="center" gap={2}>
                                <Text className="large-font">
                                  {filteredNetworkRequestStore?.length}
                                </Text>
                              </Flex>
                            </StatNumber>
                            <StatHelpText
                              mt={1}
                              fontWeight={400}
                              className="x-small-font"
                              color="gray.600"
                            >
                              Number of Requests Captured
                            </StatHelpText>
                          </Stat>
                        </CardBody>
                      </Card>
                    </Flex>
                  </Box>
                </Flex>
              </Box>
              <Box w="full">
                <Input
                  placeholder="Search for data"
                  value={searchQueryInput}
                  onChange={(e) => handleSearch(e.target.value)}
                  _placeholder={{ color: '#667085' }}
                  className="small-font"
                />
              </Box>
              <Box
                scrollMarginLeft={10}
                overflow="auto"
                maxHeight="500px"
                borderWidth={1}
                borderRadius={8}
                w="full"
                border="2px solid"
                borderColor="gray.50"
              >
                <TableContainer>
                  <Table
                    variant="simple"
                    border="ActiveBorder"
                    borderRadius="4px"
                    borderColor="black"
                  >
                    <Thead>
                      <Tr>
                        <Th />
                        <Th color="black" fontWeight={600} className="x-small-font">
                          Content Type
                        </Th>
                        <Th color="black" fontWeight={600} className="x-small-font">
                          Request URL
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredNetworkRequestStore.map((e: any) => (
                        <Tr
                          key={e.id}
                          onClick={() => {
                            handleRequestClick(e);
                          }}
                          cursor="pointer"
                          _hover={{ bg: 'gray.100' }}
                        >
                          <Td>
                            <Flex justifyContent="flex-end">
                              {e?.contentType?.includes('application/json') ? (
                                <Tag
                                  borderRadius={15}
                                  bgColor="#ECFDF3"
                                  border="1.5px solid #ABEFC6"
                                  color="#067647"
                                  fontWeight={500}
                                  px={2}
                                  className="x-small-font"
                                >
                                  <Flex gap={1.5} alignItems="center">
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: '#17B26A',
                                      }}
                                    />
                                    Stable
                                  </Flex>
                                </Tag>
                              ) : (
                                <Tag
                                  borderRadius={15}
                                  bgColor="#FFFAEB"
                                  border="1.5px solid #FEDF89"
                                  color="#B54708"
                                  fontWeight={500}
                                  px={2}
                                  className="x-small-font"
                                >
                                  <Flex gap={1.5} alignItems="center">
                                    <span
                                      style={{
                                        display: 'inline-block',
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: '#F79009',
                                      }}
                                    />
                                    Beta
                                  </Flex>
                                </Tag>
                              )}
                            </Flex>
                          </Td>
                          <Td>
                            <Code
                              backgroundColor="transparent"
                              color="black"
                              className="x-small-font"
                            >
                              {e.contentType}
                            </Code>
                          </Td>
                          <Td
                            maxW="500px"
                            overflow="hidden"
                            whiteSpace="nowrap"
                            textOverflow="ellipsis"
                            className="x-small-font"
                          >
                            <Text color="black">{e.url}</Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </Box>
            </VStack>
          </Box>
        </CardBody>
      </Card>
    </>
  );
};

export default NetworkRequestsTable;
